import { createSelector, createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

// Types
export type ModalProps = {
  [key: string]: string | number | boolean | null | undefined;
};

export type Modal = {
  name: string;
  props?: ModalProps;
};

interface ModalState {
  openedModals: Modal[];
  viewedModals: Modal[];
}

// State
export const initialState: ModalState = {
  // openedModals track the modals that have been opened, it works better for dismissed/non-dismissed logic.
  // viewedModals track the unique modals that have been viewed, it works better for hide/show elements logic within the modals.

  openedModals: [],
  viewedModals: [],
};

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    setModalOpened(state, action: PayloadAction<Modal>) {
      state.openedModals.push(action.payload);

      if (!state.viewedModals) {
        state.viewedModals = [];
      }

      const alreadyViewed = state.viewedModals.find(viewedModal => {
        if (viewedModal.name === action.payload.name) {
          if (action.payload.props) {
            return Object.keys(action.payload.props).every(key => {
              return (
                action.payload.props &&
                viewedModal.props &&
                viewedModal.props[key] === action.payload.props[key]
              );
            });
          } else {
            return true;
          }
        }
        return false;
      });
      if (!alreadyViewed) {
        state.viewedModals.push(action.payload);
      }
    },
    removeModalOpened(state, action: PayloadAction<Modal>) {
      state.openedModals = state.openedModals.filter(openedModal => {
        if (openedModal.name === action.payload.name) {
          if (action.payload.props) {
            return !Object.keys(action.payload.props).every(key => {
              return (
                action.payload.props &&
                openedModal.props &&
                openedModal.props[key] === action.payload.props[key]
              );
            });
          } else {
            return false;
          }
        }
        return true;
      });
    },
  },
});

// Selectors
export const getModalState = (state: RootState) => state.modal;
export const getOpenedModals = createSelector(
  getModalState,
  state => state.openedModals,
);
export const getViewedModals = createSelector(
  getModalState,
  state => state.viewedModals || [],
);
export const getHasModalOpened = (modal: Modal) =>
  createSelector(
    getOpenedModals,
    openedModals =>
      !!openedModals.find(openedModal => {
        if (openedModal.name === modal.name) {
          if (modal.props) {
            return Object.keys(modal.props).every(key => {
              return (
                modal.props &&
                openedModal.props &&
                openedModal.props[key] === modal.props[key]
              );
            });
          } else {
            return true;
          }
        }
        return false;
      }),
  );
export const getHasModalBeenViewed = (modal: Modal) =>
  createSelector(
    getViewedModals,
    viewedModals =>
      !!viewedModals.find(viewedModal => {
        if (viewedModal.name === modal.name) {
          if (modal.props) {
            return Object.keys(modal.props).every(key => {
              return (
                modal.props &&
                viewedModal.props &&
                viewedModal.props[key] === modal.props[key]
              );
            });
          } else {
            return true;
          }
        }
        return false;
      }),
  );

export const { setModalOpened, removeModalOpened } = modalSlice.actions;
export default modalSlice.reducer;
